import { Fragment, useId } from "react";
import { Box } from "@mui/material";
import colors from "../../config/colors";
import { colorPalette } from "../../config/colorPalette";
import ListItem from "../ListItem/ListItem";

/**
 * List
 *
 * A bordered panel of ListItems with dividers between them.
 *
 * Props:
 * - items: [{ label, value, disabled? }]
 * - multi: checkboxes and an array value (default: radios and a single value)
 * - value: selected value (single) or array of values (multi)
 * - onChange: (value) => void. Gets the new value, or the new array in multi mode.
 * - name: radio group name (generated when omitted)
 * - sx: extra styles for the panel, merged last
 */
const List = ({
  items = [],
  multi = false,
  value,
  onChange,
  name,
  sx: sxOverride = {},
  ...rest
}) => {
  const generatedName = useId();

  const isChecked = (itemValue) =>
    multi ? (value || []).includes(itemValue) : value === itemValue;

  const handleChange = (itemValue) => {
    if (!multi) return onChange?.(itemValue);
    const current = value || [];
    onChange?.(
      current.includes(itemValue)
        ? current.filter((v) => v !== itemValue)
        : [...current, itemValue],
    );
  };

  return (
    <Box
      role={multi ? "group" : "radiogroup"}
      {...rest}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        padding: "3px", // Figma's 4px, less the 1px border
        boxSizing: "border-box",
        borderRadius: "8px",
        backgroundColor: colors.white,
        border: `1px solid ${colorPalette.mercuryAlpha[8]}`,
        ...sxOverride,
      }}
    >
      {items.map((item, index) => (
        <Fragment key={item.value}>
          {index > 0 && (
            <Box
              role="separator"
              sx={{
                height: "1px",
                margin: "0 12px",
                backgroundColor: colorPalette.mercuryAlpha[10],
              }}
            />
          )}
          <ListItem
            multi={multi}
            name={multi ? undefined : name || generatedName}
            checked={isChecked(item.value)}
            disabled={item.disabled}
            onChange={() => handleChange(item.value)}
          >
            {item.label}
          </ListItem>
        </Fragment>
      ))}
    </Box>
  );
};

export default List;
